import React from 'react';
import { CheckCircle, ExternalLink, Lightbulb, ArrowRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import type { ResearchResult } from '../../types';

interface ResearchResultsProps {
  results: ResearchResult[];
  mainTopic: string;
  onContinue: () => void;
  onBack?: () => void;
}

export const ResearchResults: React.FC<ResearchResultsProps> = ({
  results,
  mainTopic,
  onContinue,
  onBack,
}) => {
  const successfulResults = results.filter(r => !r.error);
  const failedResults = results.filter(r => r.error);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-green-900">
                ✅ Investigación Completada
              </h2>
              <p className="text-green-700">
                Tema: <span className="font-semibold">{mainTopic}</span>
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-green-600">
              {successfulResults.length}/{results.length}
            </div>
            <div className="text-sm text-green-700">Subtemas investigados</div>
          </div>
        </div>
      </Card>

      {/* Resultados de cada subtema */}
      <div className="space-y-4">
        {successfulResults.map((result, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-md">
                {index + 1}
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {result.topic}
                </h3>

                {/* Research/Synthesis */}
                {(result.research || result.synthesis) && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {result.research || result.synthesis}
                    </p>
                  </div>
                )}

                {/* Key Insights */}
                {result.keyInsights && result.keyInsights.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-semibold text-gray-700">
                        Insights Clave:
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {result.keyInsights.map((insight, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                          <span className="text-yellow-600 mt-1">•</span>
                          <span>{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sources */}
                {result.sources && result.sources.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <ExternalLink className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-gray-700">
                        Fuentes ({result.sources.length}):
                      </span>
                    </div>
                    <div className="space-y-2">
                      {result.sources.slice(0, 3).map((source, idx) => (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {source.title || source.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamp */}
                {result.timestamp && (
                  <div className="mt-3 text-xs text-gray-400">
                    Investigado: {new Date(result.timestamp).toLocaleString('es-ES')}
                    {result.responseTime && ` • Tiempo: ${result.responseTime}ms`}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Failed Results */}
      {failedResults.length > 0 && (
        <Card className="bg-yellow-50 border-2 border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            ⚠️ Subtemas con problemas ({failedResults.length})
          </h3>
          <ul className="space-y-1">
            {failedResults.map((result, idx) => (
              <li key={idx} className="text-sm text-yellow-800">
                • {result.topic}: {result.error || 'Error desconocido'}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Continue Button */}
      <div className="flex justify-between pt-4">
        {onBack && (
          <Button
            onClick={onBack}
            variant="outline"
            size="lg"
            className="text-gray-500 hover:text-gray-700"
          >
            Volver atrás
          </Button>
        )}
        <Button
          onClick={onContinue}
          size="lg"
          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 ml-auto"
          icon={<ArrowRight className="w-5 h-5" />}
        >
          Continuar a Generar Artículo
        </Button>
      </div>
    </div>
  );
};













